import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles, ArrowRight, TrendingUp, Star, Quote, Award,
  Heart, Users, Target, X, Calendar, MapPin, ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import PublicLayout from "../layouts/PublicLayout";

const STORIES = [
  {
    id: 1,
    org: "Adyar Ananda Bhavan",
    tier: "Inspired",
    industry: "Food & Hospitality",
    location: "Chennai, India",
    headline: "From traditional sweets to a values-led, 17,000-employee enterprise",
    excerpt: "How a 70-year-old food brand turned its rooted-in-values culture into a globally recognised CNESS-certified operating system.",
    cover: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1200&q=80",
    quote: "Our company is deeply rooted with value. It's not just a business built around numbers & profits but about giving back to the society in a very meaningful way.",
    quoteAuthor: "Pooja Srinivasa Raja",
    quoteRole: "Executive Director, Adyar Ananda Bhavan",
    metrics: [
      { label: "Employees engaged", value: "17,000+" },
      { label: "Suppliers in network", value: "10,000+" },
      { label: "CIS Score uplift", value: "+18 pts" },
    ],
    body: [
      "Adyar Ananda Bhavan (A2B) has been serving traditional Indian vegetarian cuisine for over seven decades. As the brand scaled to 17,000+ employees and 10,000 suppliers, leadership wanted a way to prove — not just claim — that values still ran the company.",
      "CNESS Inspired-tier certification gave them that proof. The six-pillar framework surfaced where culture and policy were already strong, and where supplier oversight needed sharpening. Within 90 days the team had implemented a stakeholder communication cadence and a community-impact reporting line that now feeds the board pack each quarter.",
      "The most surprising outcome? Their HR team reports a measurable increase in talent applications citing 'company values' as the deciding factor. CNESS turned heritage into a recruiting asset.",
    ],
  },
  {
    id: 2,
    org: "Shriram Capital Private Ltd",
    tier: "Luminary",
    industry: "Financial Services",
    location: "India",
    headline: "Conscious marketing as a growth engine, not a cost centre",
    excerpt: "How a financial-services leader proved that purpose-driven product narratives compound stakeholder trust and revenue together.",
    cover: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80",
    quote: "Marketing is used to create awareness. If you create awareness, the idea of a conscious product can be sold. If you have a conscious idea, where you can contribute something good to society, success will follow.",
    quoteAuthor: "Sushant K Choudhury",
    quoteRole: "Executive Director, Shriram Capital Private Ltd",
    metrics: [
      { label: "Brand recall lift", value: "+22%" },
      { label: "Customer NPS", value: "+14 pts" },
      { label: "Time to certification", value: "62 days" },
    ],
    body: [
      "When Shriram Capital approached CNESS, the brief was simple: 'We do good things, but the market doesn't see them as a coherent story.' The Luminary-tier process forced clarity — pulling stories of impact out of every business unit and stitching them into a single Conscious Business Narrative.",
      "The result was an internal communications playbook that reframed product marketing around conscious customer outcomes. Branch managers, agents and the CMO now pull from the same evidence library when telling the brand story.",
      "Within six months, brand recall lifted 22% in target segments and customer NPS climbed 14 points. The certification became more than a badge — it became the operating language of how Shriram talks about itself.",
    ],
  },
  {
    id: 3,
    org: "Rajan Eye Care Hospital",
    tier: "Inspired",
    industry: "Healthcare",
    location: "Chennai, India",
    headline: "Patient outcomes are the most honest measure of conscious leadership",
    excerpt: "How one of South India's leading eye-care providers used the CNESS framework to formalise care quality as a leadership KPI.",
    cover: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?auto=format&fit=crop&w=1200&q=80",
    quote: "What gets measured gets managed. CNESS gave us a way to measure the things we always cared about — patient outcomes, staff wellbeing, accessible care — and put them on the same dashboard as financial performance.",
    quoteAuthor: "Dr. Mohan Rajan",
    quoteRole: "Founder & Chairman, Rajan Eye Care",
    metrics: [
      { label: "Patient satisfaction", value: "4.9 / 5" },
      { label: "Free surgeries / yr", value: "12,000+" },
      { label: "Pillars improved", value: "5 / 6" },
    ],
    body: [
      "Healthcare organisations live and die by trust. Rajan Eye Care has spent decades building it through volume, outcomes and free-care commitments. The CNESS Inspired-tier process helped formalise that trust into a board-level operating system.",
      "The biggest unlock came from the Customers & Clients pillar. The team built a closed-loop patient feedback channel that now feeds clinic-level dashboards weekly. Within one renewal cycle, satisfaction climbed to 4.9/5 and free-surgery throughput grew by 38%.",
      "Conscious leadership, when measured, becomes contagious. Rajan Eye now hosts monthly conscious-healthcare roundtables for partner hospitals across South India.",
    ],
  },
];

const SuccessStoriesPage = () => {
  const navigate = useNavigate();
  const [openStory, setOpenStory] = useState(null);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-[#FFFBEB] via-white to-[#F8FAFC]" data-testid="success-stories-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FEF3C7] rounded-full mb-6">
              <Star className="w-4 h-4 text-[#FDB813]" fill="#FDB813" />
              <span className="text-sm font-medium text-[#1E3A5F]">Success Stories</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1E3A5F] mb-6 leading-[1.1]">
              Real <span className="text-[#FDB813]">conscious enterprises</span><br />
              writing their next chapter.
            </h1>
            <p className="text-lg text-gray-600">
              Behind every CNESS badge is a team that decided to make their values measurable.
              These are their stories — what changed, what worked, and what's next.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="py-10 bg-white border-y border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-4xl mx-auto text-center">
            {[
              { v: "500+", l: "Certified champions" },
              { v: "30+", l: "Countries represented" },
              { v: "97%", l: "Renewal rate" },
              { v: "+24%", l: "Avg. trust uplift" },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-3xl font-bold text-[#FDB813] mb-1">{s.v}</div>
                <div className="text-sm text-gray-500">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stories Grid */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {STORIES.map((story) => (
              <article
                key={story.id}
                onClick={() => setOpenStory(story)}
                data-testid={`story-card-${story.id}`}
                className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all cursor-pointer flex flex-col"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={story.cover}
                    alt={story.org}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F]/80 via-transparent to-transparent" />
                  <div className="absolute top-4 right-4 px-3 py-1 bg-[#FDB813] text-[#1E3A5F] text-xs font-bold rounded-full">
                    {story.tier} Tier
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <p className="text-xs font-semibold uppercase tracking-widest text-[#FDB813] mb-1">
                      {story.industry}
                    </p>
                    <h3 className="text-xl font-bold leading-tight">{story.org}</h3>
                  </div>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h4 className="text-base font-bold text-[#1E3A5F] mb-2 line-clamp-2 group-hover:text-[#FDB813] transition-colors">
                    {story.headline}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4 line-clamp-3 flex-1">
                    {story.excerpt}
                  </p>
                  <div className="grid grid-cols-3 gap-2 pt-4 border-t border-gray-100">
                    {story.metrics.map((m, i) => (
                      <div key={i} className="text-center">
                        <p className="text-sm font-bold text-[#FDB813] leading-none">{m.value}</p>
                        <p className="text-[10px] text-gray-500 mt-1 leading-tight">{m.label}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center text-sm font-semibold text-[#1E3A5F] mt-4 group-hover:text-[#FDB813]">
                    Read full story <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#FEF3C7]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award className="w-12 h-12 text-[#1E3A5F] mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-[#1E3A5F] mb-4">Could yours be the next story?</h2>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-8">
            Every certification journey begins with a 15-minute discovery conversation. Let's start yours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate("/enquiries")}
              data-testid="success-stories-cta"
              className="bg-[#1E3A5F] hover:bg-[#152a45] text-white px-8 py-6 rounded-xl"
            >
              Begin Your Journey <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              onClick={() => navigate("/directory")}
              variant="outline"
              className="border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white px-8 py-6 rounded-xl"
            >
              Browse Directory
            </Button>
          </div>
        </div>
      </section>

      {/* Story Modal */}
      {openStory && <StoryModal story={openStory} onClose={() => setOpenStory(null)} />}
    </PublicLayout>
  );
};

const StoryModal = ({ story, onClose }) => (
  <div
    className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
    onClick={onClose}
    data-testid="story-modal-overlay"
  >
    <div
      className="bg-white rounded-3xl max-w-3xl w-full mt-4 sm:mt-8 mb-8 shadow-2xl overflow-hidden"
      onClick={(e) => e.stopPropagation()}
      data-testid="story-modal"
    >
      <div className="relative h-64 overflow-hidden">
        <img src={story.cover} alt={story.org} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F]/95 via-[#1E3A5F]/40 to-transparent" />
        <button
          onClick={onClose}
          data-testid="story-modal-close"
          className="absolute top-4 right-4 w-10 h-10 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center text-white"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="absolute bottom-6 left-6 right-6 text-white">
          <span className="inline-block px-3 py-1 bg-[#FDB813] text-[#1E3A5F] text-xs font-bold rounded-full mb-3">
            {story.tier} Tier
          </span>
          <h2 className="text-3xl font-bold leading-tight">{story.org}</h2>
          <div className="flex items-center gap-4 mt-2 text-sm text-white/80">
            <span className="flex items-center"><MapPin className="w-3 h-3 mr-1" />{story.location}</span>
            <span>·</span>
            <span>{story.industry}</span>
          </div>
        </div>
      </div>

      <div className="p-8 space-y-6">
        <h3 className="text-2xl font-bold text-[#1E3A5F]">{story.headline}</h3>

        <div className="grid grid-cols-3 gap-4">
          {story.metrics.map((m, i) => (
            <div key={i} className="bg-[#FEF3C7]/40 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-[#FDB813]">{m.value}</p>
              <p className="text-xs text-gray-600 mt-1">{m.label}</p>
            </div>
          ))}
        </div>

        <div className="space-y-4">
          {story.body.map((p, i) => (
            <p key={i} className="text-gray-700 leading-relaxed">{p}</p>
          ))}
        </div>

        <div className="bg-gradient-to-br from-[#FEF3C7] to-white border-l-4 border-[#FDB813] rounded-r-2xl p-6 relative">
          <Quote className="w-10 h-10 text-[#FDB813]/20 absolute top-4 right-4" />
          <p className="text-gray-700 italic leading-relaxed mb-4">"{story.quote}"</p>
          <p className="text-sm font-bold text-[#1E3A5F]">{story.quoteAuthor}</p>
          <p className="text-xs text-gray-500">{story.quoteRole}</p>
        </div>
      </div>
    </div>
  </div>
);

export default SuccessStoriesPage;
